import React, { useState, useEffect } from "react";
import { useResusContext } from "../Resus/ResusContext";
import { useCPRLog } from "./CPRLog";
import { useCPRState } from "./CprState/CPRStateContext";
import { loadCurrentState, saveCurrentState } from "./CprState/storage";
import { cprEventEmitter, EVENTS } from "./cprEvents";
import DefibrillatorConfirmationDialog from "./MedicationDialogs/DefibrillatorConfirmationDialog";
import { useTranslation } from 'react-i18next';

import "./Defibrillator.scss";
import { getHospitalData } from "./PerHospitalResusConfig";

interface DefiAction {
	name: string;
	joulePerKg: number;
}

interface LastUsedInfo {
	time: string;
	joules?: number;
}

const Defibrillator: React.FC = () => {
	const { weight, hospital } = useResusContext();
	const { addEntry } = useCPRLog();
	const { dispatch } = useCPRState();
	const { t } = useTranslation(['resus']);
	const [lastUsedInfo, setLastUsedInfo] = useState<{ [key: string]: LastUsedInfo }>(() => {
		const savedState = loadCurrentState();
		return savedState?.defibrillator?.lastUsedInfo || {};
	});

	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [selectedDefi, setSelectedDefi] = useState<DefiAction | null>(null);

	useEffect(() => {
		const unsubscribe = cprEventEmitter.subscribe(EVENTS.RESET_CPR, () => {
			setLastUsedInfo({});
		});

		return () => unsubscribe();
	}, []);

	const getDefi = (multiplier: number): number => {
		return weight ? Math.min(multiplier * weight, 200) : 0;
	};

	const resusDrugsDefinitions = getHospitalData(hospital).drugsData;

	const defibrillatorActions =
		resusDrugsDefinitions.protocols.find((protocol) => protocol.protocolId === "cpr")?.defi || [];

	const handleDefibrillation = (defi: DefiAction) => {
		setSelectedDefi(defi);
		setShowConfirmDialog(true);
	};

	const handleConfirmDefibrillation = (finalJoules: number) => {
		if (!selectedDefi) return;

		const currentTime = new Date().toLocaleTimeString("he-IL", {
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
		});

		const defaultJoules = getDefi(selectedDefi.joulePerKg);
		const newLastUsedInfo = {
			...lastUsedInfo,
			[selectedDefi.name]: {
				time: currentTime,
				joules: finalJoules !== defaultJoules ? finalJoules : undefined,
			},
		};

		setLastUsedInfo(newLastUsedInfo);
		saveCurrentState(
			{
				defibrillator: {
					lastUsedInfo: newLastUsedInfo,
				},
			},
			"defibrillator"
		);

		dispatch({ type: "INCREMENT_SHOCK" });

		addEntry({
			timestamp: new Date().toISOString(),
			text: t('defibrillator.logEntry', { name: selectedDefi.name, joules: finalJoules }),
			type: "action",
			isImportant: false,
		});

		setSelectedDefi(null);
	};

	return (
		<div className="Defibrillator" style={{ marginTop: "15px", marginBottom: "15px" }}>
			{!weight && (
				<div
					style={{
						color: "red",
						textAlign: "right",
						marginBottom: "20px",
						direction: "rtl",
						fontWeight: "bold",
					}}
				>
					{t('defibrillator.enterWeightFirst')}
				</div>
			)}

			<div className="Cards">
				{defibrillatorActions.map((defi, index) => (
					<div className="Card" key={index}>
						<div className="Card-body">
							<div className="content">
								<span
									style={{
										textAlign: "left",
									}}
								>
									{defi.name}
								</span>
								<span
									style={{
										textAlign: "left",
									}}
								>
									{weight && (
										<span style={{ fontWeight: "bold" }}>
											{getDefi(defi.joulePerKg)} ({defi.joulePerKg}J/Kg)
										</span>
									)}
								</span>
							</div>
							<div className="actions">
								<button
									onClick={() => handleDefibrillation(defi)}
									className="btn btn-reset"
									style={{
										padding: "8px 16px",										
										cursor: "pointer",
										minWidth: "max-content", // Ensures button doesn't get too wide
									}}
								>
									{t('defibrillator.executeShock')}
								</button>
							</div>
						</div>
						<div className="Card-body">
							<div className="content">
							{lastUsedInfo[defi.name]?.joules && (
								<div style={{textAlign: "left", fontSize: '0.9em', color: 'rgb(102, 102, 102)'}}>											
									{t('defibrillator.givenWith')}
									<span> {lastUsedInfo[defi.name].joules}J</span>
								</div>										
							)}
							</div>
							<div className="actions">
							{lastUsedInfo[defi.name] && (
							<div
								style={{
									fontSize: "0.9em",
									color: "#666",
									overflow: "hidden",
									textOverflow: "ellipsis",
									padding: "0 4px",
									boxSizing: "border-box",
									marginTop: "8px",
									wordWrap: "break-word",
									direction: "rtl",
									display: "flex",
									gap: "8px",
								}}
							>
								<span>
									{t('defibrillator.lastPerformedAt')} <span>{lastUsedInfo[defi.name].time}</span>
								</span>
							</div>
						)}
							</div>
						</div>
					</div>
				))}
			</div>

			{selectedDefi && showConfirmDialog && (
				<DefibrillatorConfirmationDialog
					isOpen={showConfirmDialog}
					setIsOpen={setShowConfirmDialog}
					defaultJoules={getDefi(selectedDefi.joulePerKg)}
					onConfirm={handleConfirmDefibrillation}
				/>
			)}
		</div>
	);
};

export default Defibrillator;